import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, Calendar, Coffee, Home, Image, LayoutDashboard, LogOut, Mail, Megaphone, Plug, ReceiptText, Star, Users, UserCog } from "lucide-react";
import OverviewTab from "./admin/OverviewTab";
import AnalyticsTab from "./admin/AnalyticsTab";
import BookingsTab from "./admin/BookingsTab";
import CabinsTab from "./admin/CabinsTab";
import ExperiencesTab from "./admin/ExperiencesTab";
import InquiriesTab from "./admin/InquiriesTab";
import ReviewsTab from "./admin/ReviewsTab";
import NewsletterTab from "./admin/NewsletterTab";
import GuestsTab from "./admin/GuestsTab";
import IntegrationsTab from "./admin/IntegrationsTab";
import EmployeesTab from "./admin/EmployeesTab";
import AccountingTab from "./admin/AccountingTab";
import GalleryTab from "./admin/GalleryTab";

const COMPONENTS = {
    overview: OverviewTab,
    analytics: AnalyticsTab,
    bookings: BookingsTab,
    cabins: CabinsTab,
    experiences: ExperiencesTab,
    inquiries: InquiriesTab,
    reviews: ReviewsTab,
    newsletter: NewsletterTab,
    guests: GuestsTab,
    accounting: AccountingTab,
    gallery: GalleryTab,
    settings: IntegrationsTab,
    employees: EmployeesTab,
};

export default function Admin() {
    const { user, logout } = useAuth();
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();

    const isAdmin = user?.role === "admin";
    const perms = user?.permissions || [];

    // Section-scoped tabs (grantable to employees). "settings" + "employees" are admin-only.
    const sectionNav = useMemo(() => [
        { key: "overview", icon: <LayoutDashboard size={16} />, label: t("admin.overview"), testid: "admin-tab-overview" },
        { key: "analytics", icon: <BarChart3 size={16} />, label: t("admin.analytics"), testid: "admin-tab-analytics" },
        { key: "bookings", icon: <Calendar size={16} />, label: t("admin.reservations"), testid: "admin-tab-reservations" },
        { key: "cabins", icon: <Home size={16} />, label: t("admin.cabins"), testid: "admin-tab-cabins" },
        { key: "experiences", icon: <Coffee size={16} />, label: t("admin.experiences"), testid: "admin-tab-experiences" },
        { key: "inquiries", icon: <Mail size={16} />, label: t("admin.inquiries"), testid: "admin-tab-inquiries" },
        { key: "reviews", icon: <Star size={16} />, label: t("admin.reviews"), testid: "admin-tab-reviews" },
        { key: "newsletter", icon: <Megaphone size={16} />, label: t("admin.newsletter"), testid: "admin-tab-newsletter" },
        { key: "guests", icon: <Users size={16} />, label: t("admin.guests"), testid: "admin-tab-guests" },
        { key: "accounting", icon: <ReceiptText size={16} />, label: t("admin.accounting"), testid: "admin-tab-accounting" },
        { key: "gallery", icon: <Image size={16} />, label: t("admin.gallery"), testid: "admin-tab-gallery" },
    ], [t]);

    const adminNav = useMemo(() => [
        { key: "employees", icon: <UserCog size={16} />, label: t("admin.employees"), testid: "admin-tab-employees" },
        { key: "settings", icon: <Plug size={16} />, label: t("admin.settings"), testid: "admin-tab-settings" },
    ], [t]);

    const visibleSection = sectionNav.filter((n) => isAdmin || perms.includes(n.key));
    const visibleAdmin = isAdmin ? adminNav : [];
    const allVisible = [...visibleSection, ...visibleAdmin];

    const [tab, setTab] = useState(() => allVisible[0]?.key || "overview");
    const ActiveTab = COMPONENTS[tab];

    if (allVisible.length === 0) {
        return (
            <div data-testid="admin-no-access" className="min-h-screen flex items-center justify-center bg-bone text-center px-6">
                <div>
                    <h1 className="font-serif text-3xl mb-3">{t("admin.noAccessTitle")}</h1>
                    <p className="text-muted2 mb-6">{t("admin.noAccessBody")}</p>
                    <button onClick={async () => { await logout(); navigate("/"); }} className="text-xs uppercase tracking-widest2 text-forest">{t("admin.signOut")}</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="min-h-screen bg-bone">
            <div className="flex">
                {/* Sidebar */}
                <aside className="hidden lg:flex w-72 min-h-screen bg-forestDark text-bone flex-col p-8 sticky top-0">
                    <Link to="/" className="flex items-center gap-3 mb-12">
                        <div className="w-8 h-8 rounded-full border border-bone flex items-center justify-center font-serif">w</div>
                        <div>
                            <div className="font-serif text-lg leading-none">Wild Coffee</div>
                            <div className="eyebrow text-bone/60 text-[10px] mt-1">{isAdmin ? "Admin" : "Staff"}</div>
                        </div>
                    </Link>
                    <nav className="space-y-1">
                        {visibleSection.map((n) => (
                            <SideLink key={n.key} active={tab === n.key} onClick={() => setTab(n.key)} icon={n.icon} label={n.label} testid={n.testid} />
                        ))}
                        {visibleAdmin.length > 0 && <div className="hairline opacity-20 my-4" />}
                        {visibleAdmin.map((n) => (
                            <SideLink key={n.key} active={tab === n.key} onClick={() => setTab(n.key)} icon={n.icon} label={n.label} testid={n.testid} />
                        ))}
                    </nav>
                    <div className="mt-auto pt-12">
                        <div className="hairline opacity-30 mb-6" />
                        <div className="flex items-center gap-1 mb-6" data-testid="admin-lang-switcher">
                            <button
                                onClick={() => setLang("en")}
                                className={`px-2 py-1 text-[11px] uppercase tracking-widest2 transition-colors ${lang === "en" ? "text-bone" : "text-bone/40 hover:text-bone/70"}`}
                                data-testid="admin-lang-en"
                            >EN</button>
                            <span className="text-bone/30 text-xs">/</span>
                            <button
                                onClick={() => setLang("es")}
                                className={`px-2 py-1 text-[11px] uppercase tracking-widest2 transition-colors ${lang === "es" ? "text-bone" : "text-bone/40 hover:text-bone/70"}`}
                                data-testid="admin-lang-es"
                            >ES</button>
                        </div>
                        <div className="text-xs text-bone/70 mb-1">{user?.name}</div>
                        <div className="text-xs text-bone/50 mb-4">{user?.email}</div>
                        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout" className="text-xs uppercase tracking-widest2 inline-flex items-center gap-2 text-bone/80 hover:text-terracotta">
                            <LogOut size={14} /> {t("admin.signOut")}
                        </button>
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0 p-6 lg:p-12">
                    <div className="lg:hidden mb-8">
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList className="flex flex-nowrap gap-2 h-auto overflow-x-auto justify-start -mx-6 px-6 py-1">
                                {allVisible.map((n) => (
                                    <TabsTrigger key={n.key} value={n.key} data-testid={`m-${n.testid}`} className="shrink-0 whitespace-nowrap gap-1.5">{n.icon}{n.label}</TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </div>

                    {ActiveTab && <ActiveTab />}
                </main>
            </div>
        </div>
    );
}

function SideLink({ active, onClick, icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm tracking-wide transition-colors ${active ? "bg-bone/10 text-bone" : "text-bone/70 hover:text-bone hover:bg-bone/5"}`}
        >
            {icon} {label}
        </button>
    );
}
